import type { FacilityID, FacilityStorageLocationID, UserID } from '../../tri-shared/types/db.ts';

export type TrainingProfileStatus = 'draft' | 'published' | 'retired';
export type TrainingRequirementCategory =
  | 'initial-training'
  | 'annual-review'
  | 'continuing-training'
  | 'location-specific'
  | 'emergency-response'
  | 'job-experience';
export type TrainingRequirementStatus =
  | 'not-started'
  | 'in-progress'
  | 'awaiting-verification'
  | 'complete'
  | 'due-soon'
  | 'overdue'
  | 'waived'
  | 'satisfied-by-equivalent-training';
export type TrainingAuthorizationStatus =
  | 'current'
  | 'training-due-soon'
  | 'training-overdue'
  | 'initial-training-in-progress'
  | 'supervised-work-only'
  | 'not-authorized';
export type TrainingAssignmentSourceType =
  | 'profile'
  | 'supplemental-profile'
  | 'responsibility'
  | 'location-type'
  | 'storage-location'
  | 'emergency-role'
  | 'individual';
export type TrainingDeadlineRule =
  | { type: 'fixed-date'; date: string }
  | { type: 'days-after-assignment'; days: number }
  | { type: 'months-after-assignment'; months: number }
  | { type: 'six-months-after-latest-assignment' }
  | { type: 'annual-review-from-completion' }
  | { type: 'annual-facility-cycle'; month: number; day: number }
  | { type: 'custom-recurrence'; months: number }
  | { type: 'no-expiration' };
export type TrainingEmergencyCoverageState =
  | 'required'
  | 'not-applicable-position'
  | 'not-applicable-facility'
  | 'covered-by-requirement'
  | 'satisfied-by-equivalent-osha-training';

export interface TrainingDateContext {
  assignmentDate: string;
  employmentDate: string | null;
  facilityAssignmentDate: string | null;
  positionAssignmentDate: string | null;
  verifiedCompletionDate: string | null;
  facilityAnnualReviewMonth: number | null;
  facilityAnnualReviewDay: number | null;
}

export interface TrainingRequirementApplication {
  requirementVersionID: number;
  requirementName: string;
  prerequisiteForUnsupervisedWork: boolean;
  status: TrainingRequirementStatus;
  dueDate: string | null;
}

export interface TrainingStatusSummary {
  status: TrainingAuthorizationStatus;
  isPermittedToWorkUnsupervised: boolean;
  explanation: string;
}

export interface StorageResponsibilityInput {
  facilityID: FacilityID;
  facilityStorageLocationID: FacilityStorageLocationID;
  userID: UserID;
  responsibilityIDs: number[];
}

export const TRAINING_EMERGENCY_TOPICS = [
  'emergency-monitoring-equipment-use',
  'emergency-monitoring-equipment-inspection',
  'emergency-monitoring-equipment-repair',
  'automatic-waste-feed-cutoff',
  'communications-systems',
  'alarm-systems',
  'fire-response',
  'explosion-response',
  'groundwater-contamination-response',
  'operations-shutdown',
  'contingency-plan-implementation',
  'spill-control-and-emergency-coordination',
] as const;

export type TrainingEmergencyTopic = (typeof TRAINING_EMERGENCY_TOPICS)[number];

export interface TrainingElementQuestion {
  key: string;
  prompt: string;
}

export interface DefaultTrainingElement {
  key: string;
  name: string;
  deliveryMethod: 'in-app' | 'in-field';
  regulatoryReference: string;
  questionnaire: readonly TrainingElementQuestion[];
}

export const DEFAULT_TRAINING_ELEMENTS = [
  {
    key: 'emergency-monitoring-equipment',
    name: 'Procedures for using, inspecting, repairing, and replacing facility emergency and monitoring equipment',
    deliveryMethod: 'in-field',
    regulatoryReference: '40 CFR 262.17(a)(7)',
    questionnaire: [
      {
        key: 'equipment-and-locations',
        prompt: 'What emergency and monitoring equipment is available, and where is each item located?',
      },
      { key: 'use-and-inspection', prompt: 'What procedures must personnel follow to use and inspect this equipment?' },
      {
        key: 'repair-and-replacement',
        prompt: 'Who coordinates repair or replacement, and what is the process when equipment is unavailable?',
      },
      { key: 'records-and-frequency', prompt: 'What inspection frequency and records apply to this equipment?' },
    ],
  },
  {
    key: 'automatic-waste-feed-cutoff',
    name: 'Key parameters for automatic waste feed cut-off systems',
    deliveryMethod: 'in-field',
    regulatoryReference: '40 CFR 262.17(a)(7)',
    questionnaire: [
      {
        key: 'systems-and-locations',
        prompt: 'Which automatic waste feed cut-off systems are present, and where are they used?',
      },
      {
        key: 'parameters-and-alarms',
        prompt: 'What operating parameters, alarms, or conditions trigger each cut-off system?',
      },
      {
        key: 'operator-response',
        prompt: 'What must personnel do when a cut-off activates, including notification and documentation?',
      },
      {
        key: 'testing-and-fallback',
        prompt: 'How are these systems tested and what controls apply if a system is unavailable?',
      },
    ],
  },
  {
    key: 'communications-and-alarm-systems',
    name: 'Communications or alarm systems',
    deliveryMethod: 'in-field',
    regulatoryReference: '40 CFR 262.17(a)(7)',
    questionnaire: [
      {
        key: 'systems-and-signals',
        prompt: 'What communications and alarm systems are used, and what does each signal or notification mean?',
      },
      {
        key: 'activation-and-notification',
        prompt: 'How are alarms activated and who must be notified, in what order?',
      },
      {
        key: 'external-communications',
        prompt: 'What external emergency contacts and communication methods are available?',
      },
      {
        key: 'testing-and-backup',
        prompt: 'How are systems tested and what backup methods are used if primary communications fail?',
      },
    ],
  },
  {
    key: 'fire-and-explosion-response',
    name: 'Response to fires or explosions',
    deliveryMethod: 'in-field',
    regulatoryReference: '40 CFR 262.17(a)(7)',
    questionnaire: [
      {
        key: 'immediate-actions',
        prompt: 'What immediate actions must personnel take when a fire or explosion occurs?',
      },
      {
        key: 'evacuation-and-accountability',
        prompt: 'What are the evacuation routes, assembly areas, and accountability procedures?',
      },
      {
        key: 'firefighting-limits',
        prompt: 'When may personnel attempt initial fire control, and when must they evacuate and wait for responders?',
      },
      {
        key: 'site-specific-hazards',
        prompt: 'What site-specific hazards, isolation steps, or responder information must be communicated?',
      },
    ],
  },
  {
    key: 'groundwater-contamination-response',
    name: 'Response to ground-water contamination incidents',
    deliveryMethod: 'in-field',
    regulatoryReference: '40 CFR 262.17(a)(7)',
    questionnaire: [
      {
        key: 'pathways-and-sensitive-areas',
        prompt: 'What drains, wells, waterways, or other sensitive areas could be affected by a release?',
      },
      {
        key: 'immediate-controls',
        prompt:
          'What immediate containment, shutoff, and notification actions are required for a potential groundwater impact?',
      },
      {
        key: 'monitoring-and-contractors',
        prompt: 'What monitoring, sampling, or specialist contractor procedures apply?',
      },
      {
        key: 'reporting-and-records',
        prompt: 'What internal or external reporting and recordkeeping are required after an incident?',
      },
    ],
  },
  {
    key: 'operations-shutdown',
    name: 'Shutdown of operations',
    deliveryMethod: 'in-field',
    regulatoryReference: '40 CFR 262.17(a)(7)',
    questionnaire: [
      { key: 'shutdown-triggers', prompt: 'What conditions require an operational shutdown or work stoppage?' },
      {
        key: 'shutdown-steps',
        prompt:
          'What steps secure waste handling, containers, equipment, utilities, and affected areas during shutdown?',
      },
      { key: 'authority-and-notification', prompt: 'Who has authority to order shutdown, and who must be notified?' },
      {
        key: 'restart-conditions',
        prompt: 'What inspection, authorization, and documentation are required before operations restart?',
      },
    ],
  },
] as const satisfies readonly DefaultTrainingElement[];

export type DefaultTrainingElementKey = (typeof DEFAULT_TRAINING_ELEMENTS)[number]['key'];

export const SYSTEM_RESPONSIBILITIES = [
  ['adding-waste', 'Adding waste to containers or tanks', 'waste-handling'],
  ['container-closure', 'Container closure', 'container-management'],
  ['container-condition-checks', 'Container condition checks', 'inspection'],
  ['labeling-hazard-indication', 'Labeling and hazard indication', 'container-management'],
  ['accumulation-start-date', 'Accumulation start date marking', 'container-management'],
  ['weekly-inspections', 'Weekly inspections', 'inspection'],
  ['inspection-issue-correction', 'Inspection issue correction', 'inspection'],
  ['compatibility-review', 'Compatibility review', 'waste-handling'],
  ['segregation-incompatible-wastes', 'Segregation of incompatible wastes', 'waste-handling'],
  ['saa-to-caa-movement', 'Movement from satellite to central accumulation', 'waste-handling'],
  ['accumulation-time-tracking', 'Accumulation time tracking', 'recordkeeping'],
  ['emergency-communications', 'Emergency communications', 'emergency-response'],
  ['alarm-activation', 'Alarm activation', 'emergency-response'],
  ['spill-response', 'Spill response', 'emergency-response'],
  ['fire-explosion-response', 'Fire or explosion response', 'emergency-response'],
  ['emergency-equipment-use', 'Emergency equipment use', 'emergency-response'],
  ['emergency-equipment-inspection', 'Emergency equipment inspection', 'emergency-response'],
  ['emergency-equipment-repair', 'Emergency equipment repair or replacement coordination', 'emergency-response'],
  ['waste-feed-cutoff-response', 'Waste feed cutoff system response', 'emergency-response'],
  ['groundwater-incident-response', 'Groundwater contamination incident response', 'emergency-response'],
  ['operations-shutdown', 'Shutdown of operations', 'emergency-response'],
  ['manifest-preparation-review', 'Manifest preparation or review', 'shipping'],
  ['waste-shipment-coordination', 'Waste shipment coordination', 'shipping'],
  ['recordkeeping', 'Recordkeeping', 'recordkeeping'],
  ['emergency-coordinator-duties', 'Emergency coordinator duties', 'emergency-response'],
  ['contingency-plan-implementation', 'Contingency plan implementation', 'emergency-response'],
] as const;
